import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";

/**
 * Canvas firma ottimizzato per touch (dito), pennino soft-touch e mouse.
 * - Usa Pointer Events: gestisce iOS/Android stylus + finger + Apple Pencil.
 * - Applica pressure (se disponibile) per dare spessore variabile alla traccia.
 * - Disabilita scroll/zoom durante la firma (touch-action: none).
 * - Gestisce HiDPI (devicePixelRatio) per una traccia nitida.
 *
 * Props:
 *  - onChange?(dataUrl|null)  invocato quando la firma cambia (dataUrl PNG o null se vuota)
 *  - color (default "#0A0A0A")
 *  - height (default 220)
 *  - placeholder (testo di guida)
 *
 * Imperative ref API:
 *  - clear()
 *  - isEmpty()
 *  - getDataURL()        -> PNG dataURL
 *  - getTrimmedDataURL() -> PNG ritagliato attorno alla firma (consigliato per PDF)
 */
const SignatureCanvas = forwardRef(function SignatureCanvas(
  { onChange, color = "#0A0A0A", height = 220, placeholder = "Firma qui con il dito o con il pennino" },
  ref
) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const dprRef = useRef(1);
  const dirtyRef = useRef(false);
  const [empty, setEmpty] = useState(true);

  // Resize / HiDPI setup
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      dprRef.current = dpr;
      const rect = wrap.getBoundingClientRect();
      const w = Math.max(280, rect.width);
      const h = height;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      // Preserva la firma esistente
      const prev = dirtyRef.current ? canvas.toDataURL("image/png") : null;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = color;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, w, h);
      if (prev) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, w, h);
        img.src = prev;
      }
    };

    setup();
    const ro = new ResizeObserver(setup);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [height, color]);

  const ptFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, p: e.pressure || 0 };
  };

  const widthFor = (e) => {
    // Per pennino: usa pressione; per dito/mouse: spessore fisso modulato.
    if (e.pointerType === "pen") {
      const p = e.pressure > 0 ? e.pressure : 0.5;
      return 0.6 + p * 3.0; // 0.6 → 3.6
    }
    return 2.0;
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    lastPtRef.current = ptFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = widthFor(e);
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    // Punto singolo (tocco breve)
    ctx.lineTo(lastPtRef.current.x + 0.01, lastPtRef.current.y + 0.01);
    ctx.stroke();
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const cur = ptFromEvent(e);
    const last = lastPtRef.current;
    ctx.lineWidth = widthFor(e);
    // Smoothing con curva quadratica (mid-point)
    const mx = (last.x + cur.x) / 2;
    const my = (last.y + cur.y) / 2;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.quadraticCurveTo(last.x, last.y, mx, my);
    ctx.stroke();
    lastPtRef.current = cur;
    dirtyRef.current = true;
    if (empty) setEmpty(false);
  };

  const finish = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    canvasRef.current.releasePointerCapture?.(e.pointerId);
    if (onChange) {
      const url = canvasRef.current.toDataURL("image/png");
      onChange(url);
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const w = c.clientWidth;
    const h = c.clientHeight;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.restore();
    dirtyRef.current = false;
    setEmpty(true);
    if (onChange) onChange(null);
  };

  const getDataURL = () => canvasRef.current.toDataURL("image/png");

  const getTrimmedDataURL = () => {
    // Ritaglia attorno alla traccia per evitare PDF con firma minuscola.
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const { width, height: H } = c;
    const data = ctx.getImageData(0, 0, width, H).data;
    let minX = width, minY = H, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < H; y += 2) {
      for (let x = 0; x < width; x += 2) {
        const idx = (y * width + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        // Ignora pixel bianchi/quasi-bianchi
        if (r < 240 || g < 240 || b < 240) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }
    if (!found) return c.toDataURL("image/png");
    const pad = 12;
    const sx = Math.max(0, minX - pad);
    const sy = Math.max(0, minY - pad);
    const sw = Math.min(width - sx, maxX - minX + pad * 2);
    const sh = Math.min(H - sy, maxY - minY + pad * 2);
    const out = document.createElement("canvas");
    out.width = sw;
    out.height = sh;
    const octx = out.getContext("2d");
    octx.drawImage(c, sx, sy, sw, sh, 0, 0, sw, sh);
    return out.toDataURL("image/png");
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => empty,
    getDataURL,
    getTrimmedDataURL,
  }));

  return (
    <div ref={wrapRef} className="w-full" data-testid="signature-canvas-wrap">
      <div className="relative rounded-xl border-2 border-dashed border-[#3B82F6]/40 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={finish}
          onPointerCancel={finish}
          onPointerLeave={(e) => drawingRef.current && finish(e)}
          style={{
            display: "block",
            width: "100%",
            height: `${height}px`,
            touchAction: "none",
            cursor: "crosshair",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
          }}
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-[#C8C8C8] text-sm">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2 opacity-70">
              <path d="M3 21l3-1 11-11a2 2 0 0 0-3-3L3 17v4z"/>
              <path d="M14.5 5.5l3 3"/>
            </svg>
            <span>{placeholder}</span>
          </div>
        )}
        {/* Linea di firma */}
        <div className="pointer-events-none absolute left-6 right-6 bottom-8 border-b border-[#0A0A0A]/15"/>
        <div className="pointer-events-none absolute left-6 bottom-3 text-[10px] uppercase tracking-widest text-[#C8C8C8]">Firma</div>
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-[#757575]">Compatibile con dito, Apple Pencil e pennini soft-touch</span>
        <button
          type="button"
          onClick={clear}
          data-testid="signature-clear-btn"
          className="text-[#7C3AED] hover:underline font-semibold"
        >Cancella</button>
      </div>
    </div>
  );
});

export default SignatureCanvas;
